// LIBRARIES
import React, { useEffect } from "react";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components/macro";
import { useT, useLanguages } from "@transifex/react";
import { getCurrentDateTimeFormatted } from "../../../helpers/DateTime";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import queryString from "query-string";
import ContentWithHeader from "../../../wrapper/ContentWithHeader/ContentWithHeader";
import { useLogger } from "../../../hooks/useLogger";
import { parseBookDeepLinkParams } from "../../../helpers/DeepLinkParams";

export interface CreateAnonymousWrapperProps {}

const CreateAnonymousWrapperDiv = styled.div``;

const CreateAnonymousWrapper: React.FC<CreateAnonymousWrapperProps> = (props) => {
    const { setTestName, loadInitArticleIdData, initAppWithBookAndUnit } = useTestCreationContext();
    const { setCreatingMode, setUserInfo, setIsIframeMode, setIsAnonFromTeacherArea } = useGeneralContext();
    const { logEvent } = useLogger();

    const currentDate = getCurrentDateTimeFormatted();

    const t = useT();
    const languages = useLanguages();

    const t_giveADescriptionPlaceholder = t("Test from {currentDate}", {
        currentDate,
        _tags: "CreateTest,CreateAnonymousWrapper",
    });

    const history = useHistory();

    const { search } = useLocation();
    const urlParams = queryString.parse(search.replace("?", ""));

    function setAnonymousUserData(disableIframeMode = false) {
        setUserInfo({
            isAnonymousUser: true,
            userName: "anonymous",
            userEmail: "",
            visitorOrUserRole: "other",
            isMobileApp: false,
            isTeacherVerified: false,
            isInternalUser: false,
        });
        setIsIframeMode(!disableIframeMode);
    }

    useEffect(() => {
        if (languages.length) {
            logEvent("Test Creation Started", {
                entry_point: "anonymous",
                creating_mode: CreationModes.TEST,
                is_deep_link: !!(urlParams.articleId || urlParams.articleUuid),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languages.length]);

    useEffect(() => {
        //make sure routing only happend after transifex responded with updated data
        if (languages.length) {
            const bookDeepLink = parseBookDeepLinkParams(urlParams);
            setAnonymousUserData(!!urlParams.fullScreen);
            setCreatingMode(CreationModes.TEST);
            if (urlParams.articleId) {
                let aId: string = urlParams.articleId as string;
                loadInitArticleIdData(aId).finally(() => {
                    history.push("/create/select-book");
                });
            } else if (bookDeepLink) {
                initAppWithBookAndUnit(bookDeepLink.articleUuid, bookDeepLink.unitFromUuid, bookDeepLink.unitToUuid)
                    .then((redirectTo) => {
                        history.push(redirectTo === "BOOK" ? "/create/select-book" : "/create/select-vocabulary");
                    })
                    .catch(() => {
                        history.push("/create/select-book");
                    });
            } else {
                setIsAnonFromTeacherArea(true);
                history.push("/create/select-book");
            }
        }
        // `search`, not the parsed params: a repeated param parses to a new array on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [languages.length, search]);

    useEffect(() => {
        setTestName(t_giveADescriptionPlaceholder);
    }, [setTestName, t_giveADescriptionPlaceholder]);

    return (
        <ContentWithHeader
            hasGrayBackground
            id={"content-with-header"}
        >
            <CreateAnonymousWrapperDiv>loading....</CreateAnonymousWrapperDiv>
        </ContentWithHeader>
    );
};

export default CreateAnonymousWrapper;
